import { isFinite, isNumber, isString } from 'lodash';

/**
 * Narrowing guards for values of unknown type, for validating data that crosses a trust
 * boundary — a `postMessage` payload, a parsed JSON body, a URL parameter.
 *
 * Guards lodash already provides as type predicates (`isString`, `isBoolean`, `isArray`,
 * `isNumber`) are not repeated here; import those from lodash. The ones below either have no
 * lodash equivalent, or exist because the lodash version returns a plain `boolean` and so
 * cannot narrow: `isPlainObject` and `isFinite` are both in that category.
 */

/** An index-accessible object. Excludes `null` and arrays, so `value['key']` is safe afterwards. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A string holding at least one character. */
export const isNonEmptyString = (value: unknown): value is string =>
  isString(value) && value.length > 0;

/** A usable number: rejects `NaN` and both infinities, which `isNumber` admits. */
export const isFiniteNumber = (value: unknown): value is number =>
  isNumber(value) && isFinite(value);

/** Membership of a literal set, typically the `as const` tuple behind a union type. */
export const isOneOf =
  <T extends string>(values: readonly T[]) =>
  (value: unknown): value is T =>
    isString(value) && (values as readonly string[]).includes(value);

/** An array whose every element satisfies `guard`. An empty array passes. */
export const isArrayOf =
  <T>(guard: (value: unknown) => value is T) =>
  (value: unknown): value is T[] =>
    Array.isArray(value) && value.every(guard);

/** Optional-field guard: an absent value passes, `null` does not. */
export const isUndefinedOr =
  <T>(guard: (value: unknown) => value is T) =>
  (value: unknown): value is T | undefined =>
    value === undefined || guard(value);
